// app/lib/xero.ts
import { XeroClient } from "xero-node";
import { getServerEnv } from "./server";
import { loadTokenSet, saveTokenSet } from "./server.tokens";

const SCOPES = [
	"openid",
	"profile",
	"email",
	"accounting.transactions",
	"accounting.contacts",
	"accounting.settings.read",
	"offline_access",
];

const newClient = () => {
	const serverEnv = getServerEnv();

	return new XeroClient({
		clientId: serverEnv.xeroClientId,
		clientSecret: serverEnv.xeroClientSecret,
		redirectUris: [serverEnv.xeroRedirectUri],
		scopes: SCOPES,
	});
}
	
	let refreshPromise: Promise<void> | null = null;

async function ensureFreshToken(client: XeroClient) {
	const tokenSet = client.readTokenSet?.();

	const now = Math.floor( Date.now() / 1000 );

	if (tokenSet?.expires_at && tokenSet.expires_at <= now + 60) {
		if (!refreshPromise) {
			refreshPromise = (async () => {
				await client.refreshToken();
				await saveTokenSet(client.readTokenSet()!);
				await client.updateTenants();
			})();
		}
		await refreshPromise;
		refreshPromise = null;
	}
};

async function createReadyXeroClient(): Promise<XeroClient> {
	const xero = newClient();

	// Ensure OpenID client is ready for refreshToken()
	if (typeof (xero as any).initialize === "function") {
		await (xero as any).initialize();
	}

	const saved = await loadTokenSet();
	if (!saved) {
		return xero; // not authorized yet
	}
	
	// Use the saved JSON directly; don't depend on TokenSet methods
	xero.setTokenSet( saved as any );

	await ensureFreshToken(xero);

	{/* OUT 
	
	// Robust expiry check (works with serialized tokens)
	const ts: any = xero.readTokenSet();
	const isExpired =
		typeof ts?.expires_at === "number"
			? ts.expires_at * 1000 <= Date.now() + 60_000 // refresh 60s early
			: false;

	if (isExpired) {
		if (!ts?.refresh_token) throw new Error("Saved token has no refresh_token");
		await xero.refreshToken(); // <-- now safe (client initialized)
		await saveTokenSet(xero.readTokenSet()!);

		console.log("New set saved", xero.readTokenSet());
	}
	
		await xero.updateTenants();

	*/ }


	return xero;
}

// Export a ready-to-use client (top-level await).
// This is what your app code imports and uses directly.
//export let xeroClient: XeroClient = await createReadyXeroClient();

let clientPromises = new Map<string, Promise<XeroClient>>();

/* prettier-ignore */
export async function getXeroClient( accountId = "primary" ): Promise<{ xeroClient: XeroClient; tenantId: string }> {
	"use server";

	try {
		let clientPromise = clientPromises.get(accountId);

		if (!clientPromise) {
			clientPromise = createReadyXeroClient();
			clientPromises.set(accountId, clientPromise);
		}

		const xeroClient = await clientPromise;

		// 🔄 Check expiry on reuse
		const tokenSet = xeroClient.readTokenSet?.();
		const now = Math.floor(Date.now() / 1000);

		if (tokenSet?.expires_at && tokenSet.expires_at <= now + 60) {
			await xeroClient.refreshToken();
			await saveTokenSet(xeroClient.readTokenSet());
		}
		await xeroClient.updateTenants();

		if(xeroClient.tenants?.[0]?.tenantId) {
			return { xeroClient, tenantId: xeroClient.tenants?.[0]?.tenantId };
		} else {
			throw new Error("Accounts not currently available");
		}
	} catch (error) {
		console.error("Error getting Xero client:", error);
		throw error;
	}
}

// Helpers for the OAuth flow
export async function getConsentUrl(accountId = "primary", nonce?: string) {
	"use server";
	const x = newClient();
	const state = JSON.stringify({ a: accountId, n: nonce ?? "" });
	return x.buildConsentUrl(state);
}

export const handleOAuthCallback = async (fullCallbackUrl: string) => {
	"use server";
	const x = newClient();
	const tokenSet = await x.apiCallback(fullCallbackUrl); // sets tokens internally
	await saveTokenSet(tokenSet);
	await x.updateTenants();
	return { tokenSet, tenants: x.tenants };
};

// Optional: refresh the exported client inside the same runtime after callback
export async function initXeroRuntime(accountId = "primary") {
	"use server";
	const x = await createReadyXeroClient();
	// @ts-ignore reassign the exported binding's internals
	(xeroClient as any).tokenSet = x.readTokenSet();
	// copy tenants and api handles to keep existing imports working
	// safest is replacing enumerable fields we rely on:
	(xeroClient as any).tenants = x.tenants;
	(xeroClient as any).accountingApi = x.accountingApi;
	(xeroClient as any).projectApi = x.projectApi;

	clientPromises.set(accountId, createReadyXeroClient(accountId));
	// etc. (xero-node attaches APIs on the instance)
}
